import { createFileRoute, Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Badge, Button, Card, EmptyState, Input, Spinner } from '@/components';
import type { StaticTermSheetListItem, TermSheetListItem } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import {
  DEFAULT_TERM_SHEET_CATEGORY,
  TERM_SHEET_CATEGORIES,
} from '@/lib/learning-hub-categories';
import { cloudTermSheetResourceKey, termSheetPracticeSearch } from '@/lib/term-sheet-practice';

function TermSheetsList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'hmonglus' | 'moob'>('hmonglus');
  const [audioVoices, setAudioVoices] = useState<'dual' | 'single'>('dual');
  const [category, setCategory] = useState(DEFAULT_TERM_SHEET_CATEGORY);
  const [requiresSubscription, setRequiresSubscription] = useState(false);

  const cloudQ = useQuery({
    queryKey: ['admin', 'term-sheets'],
    queryFn: () => apiJson<TermSheetListItem[]>('/v1/admin/term-sheets'),
  });

  const staticQ = useQuery({
    queryKey: ['admin', 'term-sheets', 'static'],
    queryFn: () => apiJson<StaticTermSheetListItem[]>('/v1/admin/term-sheets/static'),
  });

  const createSheet = useMutation({
    mutationFn: (payload: {
      name: string;
      mode: string;
      audio_voices: string;
      category: string;
      requires_subscription: boolean;
    }) =>
      apiJson<{ id: number }>('/v1/admin/term-sheets', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (created) => {
      setName('');
      setCategory(DEFAULT_TERM_SHEET_CATEGORY);
      setRequiresSubscription(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'term-sheets'] });
      void navigate({
        to: '/admin/term-sheets/$id',
        params: { id: String(created.id) },
      });
    },
  });

  const isLoading = cloudQ.isLoading || staticQ.isLoading;
  const isError = cloudQ.isError || staticQ.isError;
  const error = (cloudQ.error ?? staticQ.error) as Error | null;
  const refetch = () => {
    void cloudQ.refetch();
    void staticQ.refetch();
  };

  const cloudSheets = cloudQ.data ?? [];
  const staticSheets = staticQ.data ?? [];
  const hasAnySheets = cloudSheets.length > 0 || staticSheets.length > 0;

  const staticByCategory = useMemo(() => {
    const grouped = new Map<string, StaticTermSheetListItem[]>();
    for (const sheet of staticSheets) {
      const key = sheet.category || 'OTHER';
      grouped.set(key, [...(grouped.get(key) ?? []), sheet]);
    }
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [staticSheets]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {error?.message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Term Sheets</h1>
        <p className="mt-1 text-sm text-gray-600">
          Cloud cheatsheets (draft → live) and bundled app cheatsheets. Manage
          term content on cloud sheets; attach practice games for any sheet.
        </p>
      </div>

      <Card className="space-y-3">
        <h2 className="text-sm font-medium text-gray-900">New cloud sheet</h2>
        <div className="flex flex-wrap items-end gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sheet name (e.g. Useful Words III)"
            className="max-w-xs"
          />
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={mode}
            onChange={(e) => {
              const next = e.target.value as 'hmonglus' | 'moob';
              setMode(next);
              setAudioVoices(next === 'moob' ? 'single' : 'dual');
            }}
          >
            <option value="hmonglus">Hmonglus</option>
            <option value="moob">Moob</option>
          </select>
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={audioVoices}
            onChange={(e) => setAudioVoices(e.target.value as 'dual' | 'single')}
          >
            <option value="dual">Dual voice (adult + child)</option>
            <option value="single">Single voice</option>
          </select>
          <select
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Learning Hub category"
          >
            {TERM_SHEET_CATEGORIES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={requiresSubscription}
              onChange={(e) => setRequiresSubscription(e.target.checked)}
            />
            Subscribers only
          </label>
          <Button
            disabled={!name.trim() || createSheet.isPending}
            onClick={() =>
              createSheet.mutate({
                name: name.trim(),
                mode,
                audio_voices: audioVoices,
                category,
                requires_subscription: requiresSubscription,
              })
            }
          >
            {createSheet.isPending ? 'Creating…' : 'Create sheet'}
          </Button>
        </div>
        {createSheet.isError ? (
          <p className="text-sm text-red-600">{(createSheet.error as Error).message}</p>
        ) : null}
      </Card>

      {!hasAnySheets ? (
        <EmptyState title="No term sheets yet" />
      ) : (
        <div className="space-y-8">
          {cloudSheets.length > 0 ? (
            <section className="space-y-3">
              <h2 className="text-lg font-medium text-gray-900">Cloud sheets</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {cloudSheets.map((sheet) => (
                  <div key={sheet.id} className="space-y-2">
                    <Link
                      to="/admin/term-sheets/$id"
                      params={{ id: String(sheet.id) }}
                      className={clsx(
                        'group block rounded-[22px] focus:outline-none',
                        'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                      )}
                    >
                      <Card
                        className={clsx(
                          'h-full transition',
                          'group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]',
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-lg font-medium text-gray-900">{sheet.name}</h3>
                          <div className="flex shrink-0 flex-wrap justify-end gap-1">
                            <Badge variant="success">Cloud</Badge>
                            <Badge variant={sheet.status === 'live' ? 'success' : 'warning'}>
                              {sheet.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {sheet.mode} · {sheet.category || 'FLASHCARDS'} · {sheet.audio_voices} ·{' '}
                          {sheet.term_count} terms · v{sheet.content_version}
                        </p>
                        <p className="mt-2 font-mono text-xs text-gray-400">{sheet.slug}</p>
                        <p className="mt-3 text-sm font-medium text-primary group-hover:underline">
                          Edit sheet →
                        </p>
                      </Card>
                    </Link>
                    <Link
                      to="/admin/term-sheets/practice"
                      search={termSheetPracticeSearch({
                        resourceKey: cloudTermSheetResourceKey(sheet.slug),
                        title: sheet.name,
                        kind: 'cloud',
                        sheetId: sheet.id,
                      })}
                      className="block text-sm font-medium text-primary hover:underline"
                    >
                      Practice games →
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {staticSheets.length > 0 ? (
            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Static app cheatsheets</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Bundled in the mobile app — not editable as cloud CMS documents. You can still
                  attach and manage practice games here.
                </p>
              </div>
              {staticByCategory.map(([cat, sheets]) => (
                <div key={cat} className="space-y-3">
                  <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">
                    {cat}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {sheets.map((sheet) => (
                      <Link
                        key={sheet.resource_key}
                        to="/admin/term-sheets/practice"
                        search={termSheetPracticeSearch({
                          resourceKey: sheet.resource_key,
                          title: sheet.title,
                          kind: 'static',
                        })}
                        className={clsx(
                          'group block rounded-[22px] focus:outline-none',
                          'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                        )}
                      >
                        <Card
                          className={clsx(
                            'h-full transition',
                            'group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-lg font-medium text-gray-900">{sheet.title}</h3>
                            <Badge variant="neutral">Static</Badge>
                          </div>
                          <p className="mt-2 font-mono text-xs text-gray-400">
                            {sheet.resource_key}
                          </p>
                          <p className="mt-3 text-sm font-medium text-primary group-hover:underline">
                            Manage practice →
                          </p>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function TermSheetsPage() {
  const matches = useMatches();
  const isNested = matches.some(
    (m) =>
      m.routeId === '/admin/term-sheets/$id' ||
      m.routeId === '/admin/term-sheets/practice',
  );
  if (isNested) return <Outlet />;
  return <TermSheetsList />;
}

export const Route = createFileRoute('/admin/term-sheets')({
  component: TermSheetsPage,
});
